// src/utils/locationPrivacy.js

/**
 * Heuristically determines whether an address part looks like a POI / business
 * name rather than a city or admin region. Used to strip POI prefixes off
 * approximate-mode displays so notes tagged at a named business still respect
 * the user's "approximate" intent.
 */
function isLikelyPoiName(part) {
  if (/[()]/.test(part)) return true; // e.g. "Cafe (Milpitas)"
  // eslint-disable-next-line no-control-regex
  if (/[^\x00-\x7F]/.test(part)) return true; // non-ASCII (CJK, etc.)
  const wordCount = part.split(/\s+/).length;
  return wordCount >= 4; // e.g. "Mian Sichuan Noodle and Dumpling"
}

/**
 * Strips street-level detail AND POI/business-name prefixes from an address
 * string, keeping only city/state.
 *
 * Examples:
 *   "45060 Main St, Fremont, CA"                          → "Fremont, CA"
 *   "Blue Bottle Coffee, 300 Webster St, Oakland, CA"     → "Oakland, CA"
 *   "Mian Sichuan Noodle (Milpitas) 滋味小面, Milpitas"   → "Milpitas"
 *   "Fremont, CA"                                         → "Fremont, CA"
 *
 * @param {string} addressString - Full address string
 * @returns {string} City/state level address
 */
export function formatApproximateAddress(addressString) {
  if (!addressString || addressString === "Unknown Location") return "Unknown Location";

  const parts = addressString.split(",").map((p) => p.trim()).filter(Boolean);

  // Filter out street-level parts (contain leading numbers or street suffixes)
  const cityLevelParts = parts.filter((part) => {
    if (/^\d+\s/.test(part)) return false;
    if (/\b(St|Ave|Blvd|Dr|Rd|Ln|Way|Ct|Pl|Cir|Pkwy|Hwy|Street)\b/i.test(part)) return false;
    return true;
  });

  // Also filter out zip codes and country names
  const withoutZipOrCountry = cityLevelParts.filter((part) => {
    if (/^\d{5}(-\d{4})?$/.test(part)) return false;
    const countries = ["United States", "United States of America", "USA", "US", "Canada", "Mexico"];
    if (countries.includes(part)) return false;
    return true;
  });

  // Strip embedded zip codes from any surviving parts. Mapbox bundles
  // state+zip into a single comma chunk like "CA 95035" (and "10623 Berlin"
  // in the German format), so the standalone-zip filter above doesn't catch
  // them. Approximate mode should never expose a zip — it's pinpoint-level
  // info — so this also doubles as a privacy safeguard.
  const filtered = withoutZipOrCountry
    .map((part) =>
      part
        .replace(/\b\d{5}(-\d{4})?\b/, "")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .filter(Boolean);

  // If 3+ parts survived the street/zip/country filters, the first is almost
  // certainly a POI prefix (the typical city/state pair is just 2 parts) —
  // keep only the trailing city + state pair.
  if (filtered.length >= 3) {
    return filtered.slice(-2).join(", ") || "Unknown Location";
  }

  // If exactly 2 parts remain and the first looks POI-like, drop it. Catches
  // the sparse-data case where Mapbox returns only "POI, City" with no street
  // or state in between (e.g. POIs without a registered postal address).
  if (filtered.length === 2 && isLikelyPoiName(filtered[0])) {
    return filtered[1] || "Unknown Location";
  }

  return filtered.slice(0, 2).join(", ") || "Unknown Location";
}
